export type TypoProps = {
  element?:
    | "p"
    | "h1"
    | "h2"
    | "h3"
    | "h4"
    | "h5"
    | "h6"
    | "span"
    | "div"
    | "th"
    | "td"
    // `li` so list text carries the typo class on the list item itself — a
    // `::marker` is styled by its own `li`, not by a wrapped child, so a nested
    // span leaves the bullet at the inherited size and colour.
    | "li";
  type?: TypoStyle;
  /**
   * @deprecated Pass `lgType` alongside `smType` to set the `lg`-breakpoint
   * style explicitly. Without it, `type` is used as the `lg` style as a
   * fallback — this fallback will be removed in a future version.
   */
  smType?: TypoStyle;
  /** Style applied at the `lg` breakpoint when `smType` is set. */
  lgType?: TypoStyle;
  textColor?: string;
  /**
   * @deprecated use `className` instead. Will be removed in future versions.
   */
  classes?: string;
  className?: string;
  id?: string;
  children?: React.ReactNode;
};

export type TypoStyle =
  | "display9XL"
  | "display8XL"
  | "display7XL"
  | "display6XL"
  | "display5XL"
  | "display4XL"
  | "display3XL"
  | "heading4XL"
  | "heading3XL"
  | "heading2XL"
  | "headingXL"
  | "headingLG"
  | "headingMD"
  | "headingSM"
  | "subheadingXL"
  | "subheadingLG"
  | "subheadingMD"
  | "subheadingSM"
  | "subheadingXS"
  | "subheading2XS"
  | "labelXL"
  | "labelLG"
  | "labelMD"
  | "labelSM"
  | "labelXS"
  | "label2XS"
  | "label3XS"
  | "bodyXL"
  | "bodyLG"
  | "bodyMD"
  | "bodySM"
  | "bodyXS"
  | "body2XS"
  | "captionXL"
  | "captionLG"
  | "captionMD"
  | "captionSM"
  | "captionXS"
  | "caption2XS"
  | "legalMD"
  | "legalXS";

/**
 * Maps TypoStyle enum to CSS class names
 */
export const getTypoClassName = (type: TypoStyle): string => {
  const classMap: Record<TypoStyle, string> = {
    display9XL: "typo-display-9xl",
    display8XL: "typo-display-8xl",
    display7XL: "typo-display-7xl",
    display6XL: "typo-display-6xl",
    display5XL: "typo-display-5xl",
    display4XL: "typo-display-4xl",
    display3XL: "typo-display-3xl",
    heading4XL: "typo-heading-4xl",
    heading3XL: "typo-heading-3xl",
    heading2XL: "typo-heading-2xl",
    headingXL: "typo-heading-xl",
    headingLG: "typo-heading-lg",
    headingMD: "typo-heading-md",
    headingSM: "typo-heading-sm",
    subheadingXL: "typo-subheading-xl",
    subheadingLG: "typo-subheading-lg",
    subheadingMD: "typo-subheading-md",
    subheadingSM: "typo-subheading-sm",
    subheadingXS: "typo-subheading-xs",
    subheading2XS: "typo-subheading-2xs",
    labelXL: "typo-label-xl",
    labelLG: "typo-label-lg",
    labelMD: "typo-label-md",
    labelSM: "typo-label-sm",
    labelXS: "typo-label-xs",
    label2XS: "typo-label-2xs",
    label3XS: "typo-label-3xs",
    bodyXL: "typo-body-xl",
    bodyLG: "typo-body-lg",
    bodyMD: "typo-body-md",
    bodySM: "typo-body-sm",
    bodyXS: "typo-body-xs",
    body2XS: "typo-body-2xs",
    captionXL: "typo-caption-xl",
    captionLG: "typo-caption-lg",
    captionMD: "typo-caption-md",
    captionSM: "typo-caption-sm",
    captionXS: "typo-caption-xs",
    caption2XS: "typo-caption-2xs",
    legalMD: "typo-legal-md",
    legalXS: "typo-legal-xs",
  };

  return classMap[type] || classMap.bodyMD;
};

const lgTypoClassMap: Record<TypoStyle, string> = {
  display9XL: "lg:typo-display-9xl",
  display8XL: "lg:typo-display-8xl",
  display7XL: "lg:typo-display-7xl",
  display6XL: "lg:typo-display-6xl",
  display5XL: "lg:typo-display-5xl",
  display4XL: "lg:typo-display-4xl",
  display3XL: "lg:typo-display-3xl",
  heading4XL: "lg:typo-heading-4xl",
  heading3XL: "lg:typo-heading-3xl",
  heading2XL: "lg:typo-heading-2xl",
  headingXL: "lg:typo-heading-xl",
  headingLG: "lg:typo-heading-lg",
  headingMD: "lg:typo-heading-md",
  headingSM: "lg:typo-heading-sm",
  subheadingXL: "lg:typo-subheading-xl",
  subheadingLG: "lg:typo-subheading-lg",
  subheadingMD: "lg:typo-subheading-md",
  subheadingSM: "lg:typo-subheading-sm",
  subheadingXS: "lg:typo-subheading-xs",
  subheading2XS: "lg:typo-subheading-2xs",
  labelXL: "lg:typo-label-xl",
  labelLG: "lg:typo-label-lg",
  labelMD: "lg:typo-label-md",
  labelSM: "lg:typo-label-sm",
  labelXS: "lg:typo-label-xs",
  label2XS: "lg:typo-label-2xs",
  label3XS: "lg:typo-label-3xs",
  bodyXL: "lg:typo-body-xl",
  bodyLG: "lg:typo-body-lg",
  bodyMD: "lg:typo-body-md",
  bodySM: "lg:typo-body-sm",
  bodyXS: "lg:typo-body-xs",
  body2XS: "lg:typo-body-2xs",
  captionXL: "lg:typo-caption-xl",
  captionLG: "lg:typo-caption-lg",
  captionMD: "lg:typo-caption-md",
  captionSM: "lg:typo-caption-sm",
  captionXS: "lg:typo-caption-xs",
  caption2XS: "lg:typo-caption-2xs",
  legalMD: "lg:typo-legal-md",
  legalXS: "lg:typo-legal-xs",
};

export const Typo = ({
  element = "p",
  textColor = "text-text-primary",
  type = "bodyMD",
  smType,
  lgType,
  classes = "",
  className = "",
  id,
  children,
}: TypoProps) => {
  const typoClass = getTypoClassName(type);
  const smTypeClass = smType ? getTypoClassName(smType) : "";
  const lgTypoClass = lgType ? lgTypoClassMap[lgType] : "";

  const Element = element;

  //If both smType and lgType are provided, apply both classes. If only smType is provided, apply smType class and default to type for lg. If neither is provided, apply only the default type class.
  if (smType && lgType) {
    return (
      <Element
        id={id}
        className={`${textColor} ${smTypeClass} ${lgTypoClass} ${classes} ${className}`}
      >
        {children}
      </Element>
    );
  }

  //preserves previous inconsistent behavior of smType applying at lg breakpoint if lgType is not provided
  if (smType) {
    return (
      <Element
        id={id}
        className={`${textColor} ${smTypeClass} lg:${typoClass} ${classes} ${className}`}
      >
        {children}
      </Element>
    );
  }

  return (
    <Element
      id={id}
      className={`${textColor} ${typoClass} ${classes} ${className}`}
    >
      {children}
    </Element>
  );
};
